import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from '../../configs/axiosInstance';
import { toast } from 'react-toastify';
import { 
  Package, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Truck, 
  Calendar,
  MapPin,
  Phone,
  CreditCard,
  Receipt,
  Eye,
  RefreshCw,
  ShoppingBag,
  Download
} from 'lucide-react';

function MyOrder() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [nextPage, setNextPage] = useState(null);
  const [previousPage, setPreviousPage] = useState(null);

  useEffect(() => {
    loadOrders();
  }, []);

  const loadOrders = async (page = 1) => {
    setLoading(true);
    try {
      const response = await axios.get(`/api/bookstore/orders/my_orders/?page=${page}`);
      setOrders(response.data.results || []);
      setTotalCount(response.data.count || 0);
      setNextPage(response.data.next);
      setPreviousPage(response.data.previous);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error loading orders:', error);
      toast.error('Failed to load your orders');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const statusColors = {
      'PENDING': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'CONFIRMED': 'bg-blue-100 text-blue-800 border-blue-200',
      'PROCESSING': 'bg-purple-100 text-purple-800 border-purple-200',
      'SHIPPED': 'bg-indigo-100 text-indigo-800 border-indigo-200',
      'DELIVERED': 'bg-green-100 text-green-800 border-green-200',
      'CANCELLED': 'bg-red-100 text-red-800 border-red-200',
      'REFUNDED': 'bg-gray-100 text-gray-800 border-gray-200'
    };
    return statusColors[status?.toUpperCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getPaymentStatusColor = (status) => {
    const statusColors = {
      'PENDING': 'bg-yellow-100 text-yellow-800 border-yellow-200',
      'PROCESSING': 'bg-blue-100 text-blue-800 border-blue-200',
      'COMPLETED': 'bg-green-100 text-green-800 border-green-200',
      'FAILED': 'bg-red-100 text-red-800 border-red-200'
    };
    return statusColors[status?.toUpperCase()] || 'bg-gray-100 text-gray-800 border-gray-200';
  };

  const getStatusIcon = (status) => {
    const statusIcons = {
      'PENDING': <Clock className="w-4 h-4" />,
      'CONFIRMED': <CheckCircle className="w-4 h-4" />,
      'PROCESSING': <RefreshCw className="w-4 h-4" />,
      'SHIPPED': <Truck className="w-4 h-4" />,
      'DELIVERED': <Package className="w-4 h-4" />,
      'CANCELLED': <XCircle className="w-4 h-4" />,
      'REFUNDED': <RefreshCw className="w-4 h-4" />
    };
    return statusIcons[status?.toUpperCase()] || <Package className="w-4 h-4" />;
  };

  const getPaymentMethodColor = (method) => {
    const methodColors = {
      'BKASH': 'bg-red-100 text-red-800',
      'NAGAD': 'bg-orange-100 text-orange-800',
      'ROCKET': 'bg-purple-100 text-purple-800',
      'COD': 'bg-green-100 text-green-800'
    };
    return methodColors[method?.toUpperCase()] || 'bg-gray-100 text-gray-800';
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setSelectedOrder(null);
  };

  const handleNextPage = () => {
    if (nextPage) {
      const pageNumber = new URL(nextPage).searchParams.get('page');
      loadOrders(parseInt(pageNumber));
    }
  };

  const handlePreviousPage = () => {
    if (previousPage) {
      const pageNumber = new URL(previousPage).searchParams.get('page');
      loadOrders(parseInt(pageNumber));
    }
  };

  const downloadInvoice = async (orderNumber) => {
    try {
      const response = await axios.get(`/api/bookstore/orders/${orderNumber}/invoice/`, {
        responseType: 'blob', // Important for file download
      });
      
      // Create a blob URL and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoice_${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Invoice downloaded successfully!');
    } catch (error) {
      console.error('Error downloading invoice:', error);
      toast.error('Failed to download invoice');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 mt-20">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-8">
            <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-blue-500 mx-auto mb-4"></div>
            <p className="text-gray-600 text-lg font-medium">Loading your orders...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-12 px-4 sm:px-6 lg:px-8 mt-20">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <ShoppingBag className="w-8 h-8 text-blue-600" />
              My Orders
            </h1>
            <p className="text-gray-600 mt-2">
              Track and manage your book orders ({totalCount} total orders)
            </p>
          </div>
          <Link
            to="/book-store"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium"
          >
            Continue Shopping
          </Link>
        </div>

        {/* Orders List */}
        {orders.length === 0 ? (
          <div className="bg-white rounded-2xl shadow-xl p-12 text-center">
            <Package className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">No Orders Found</h3>
            <p className="text-gray-600 mb-6">You haven't placed any orders yet.</p>
            <Link
              to="/book-store"
              className="inline-flex items-center px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 font-medium"
            >
              Start Shopping
            </Link>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div key={order.id} className="bg-white rounded-2xl shadow-xl p-6 hover:shadow-2xl transition-all duration-300">
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                  {/* Order Info */}
                  <div className="flex-1">
                    <div className="flex items-center gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <Receipt className="w-5 h-5 text-gray-600" />
                        <span className="font-semibold text-gray-900">{order.order_number}</span>
                      </div>
                      <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-sm font-medium ${getStatusColor(order.status)}`}>
                        {getStatusIcon(order.status)}
                        {order.status}
                      </div>
                      <div className={`inline-flex items-center gap-1 px-3 py-1 rounded-full border text-sm font-medium ${getPaymentStatusColor(order.payment_status)}`}>
                        <CreditCard className="w-4 h-4" />
                        {order.payment_status}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm text-gray-600">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(order.created_at)}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4" />
                        <span className={`px-2 py-1 rounded text-xs font-medium ${getPaymentMethodColor(order.payment_method)}`}>
                          {order.payment_method}
                        </span>
                      </div>
                      <div className="flex items-center gap-2">
                        <Truck className="w-4 h-4" />
                        <span>{order.delivery_method?.replace('_', ' ')}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <MapPin className="w-4 h-4" />
                        <span>{order.delivery_city}</span>
                      </div>
                    </div>

                    {/* Items Preview */}
                    <div className="mt-4">
                      <p className="text-sm text-gray-600 mb-2">
                        {order.items?.length || 0} item(s)
                      </p>
                      {order.items && order.items.length > 0 && (
                        <div className="flex flex-wrap gap-2">
                          {order.items.slice(0, 3).map((item, index) => (
                            <span key={index} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                              {item.book_name} x{item.quantity}
                            </span>
                          ))}
                          {order.items.length > 3 && (
                            <span className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded text-xs">
                              +{order.items.length - 3} more
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Order Total & Actions */}
                  <div className="lg:text-right">
                    <div className="mb-4">
                      <p className="text-2xl font-bold text-gray-900">৳{order.total}</p>
                      <p className="text-sm text-gray-600">
                        Subtotal: ৳{order.subtotal} | Shipping: ৳{order.shipping_charge}
                      </p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2 justify-end">
                      <button
                        onClick={() => openOrderDetails(order)}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 text-sm font-medium"
                      >
                        <Eye className="w-4 h-4" />
                        View Details
                      </button>
                      <button
                        onClick={() => downloadInvoice(order.order_number)}
                        className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 text-sm font-medium"
                      >
                        <Download className="w-4 h-4" />
                        Invoice
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="flex justify-between items-center mt-8 bg-white rounded-2xl shadow-xl p-6">
            <button
              onClick={handlePreviousPage}
              disabled={!previousPage}
              className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
              </svg>
              Previous
            </button>
            
            <div className="text-center">
              <p className="text-gray-600">
                Showing page {currentPage} of {Math.ceil(totalCount / 10)}
              </p>
              <p className="text-sm text-gray-500">
                Total {totalCount} orders
              </p>
            </div>
            
            <button
              onClick={handleNextPage}
              disabled={!nextPage}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-all duration-200 text-sm font-medium"
            >
              Next
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          </div>
        )}

        {/* Order Details Modal */}
        {showModal && selectedOrder && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">Order Details</h2>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => downloadInvoice(selectedOrder.order_number)}
                      className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all duration-200 text-sm font-medium"
                    >
                      <Download className="w-4 h-4" />
                      Download Invoice
                    </button>
                    <button
                      onClick={closeModal}
                      className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      <XCircle className="w-6 h-6 text-gray-600" />
                    </button>
                  </div>
                </div>

                <div className="space-y-6">
                  {/* Order Info */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Order Information</h3>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Order Number:</span>
                        <p className="font-medium">{selectedOrder.order_number}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Status:</span>
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-medium mt-1 ${getStatusColor(selectedOrder.status)}`}>
                          {getStatusIcon(selectedOrder.status)}
                          {selectedOrder.status}
                        </div>
                      </div>
                      <div>
                        <span className="text-gray-600">Order Date:</span>
                        <p className="font-medium">{selectedOrder.created_at}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Payment Method:</span>
                        <p className="font-medium">{selectedOrder.payment_method}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Payment Status:</span>
                        <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-medium mt-1 ${getPaymentStatusColor(selectedOrder.payment_status)}`}>
                          <CreditCard className="w-3 h-3" />
                          {selectedOrder.payment_status}
                        </div>
                      </div>
                      {selectedOrder.payment_reference && (
                        <div>
                          <span className="text-gray-600">Payment Reference:</span>
                          <p className="font-medium text-sm break-all">{selectedOrder.payment_reference}</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Delivery Information */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Delivery Information
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-gray-600">Name:</span>
                        <p className="font-medium">{selectedOrder.delivery_name}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Phone:</span>
                        <p className="font-medium">{selectedOrder.delivery_phone}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Address:</span>
                        <p className="font-medium">{selectedOrder.delivery_address}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">City:</span>
                        <p className="font-medium">{selectedOrder.delivery_city}</p>
                      </div>
                      {selectedOrder.delivery_postal_code && (
                        <div>
                          <span className="text-gray-600">Postal Code:</span>
                          <p className="font-medium">{selectedOrder.delivery_postal_code}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-gray-600">Delivery Method:</span>
                        <p className="font-medium">{selectedOrder.delivery_method?.replace('_', ' ')}</p>
                      </div>
                    </div>
                  </div>

                  {/* Order Items */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                      <Package className="w-5 h-5" />
                      Order Items
                    </h3>
                    <div className="space-y-3">
                      {selectedOrder.items?.map((item, index) => (
                        <div key={index} className="flex justify-between items-center p-3 bg-white rounded-lg">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              {item.book_image && (
                                <img 
                                  src={item.book_image} 
                                  alt={item.book_name}
                                  className="w-12 h-12 object-cover rounded-lg"
                                  onError={(e) => { e.target.style.display = 'none'; }}
                                />
                              )}
                              <div>
                                <p className="font-medium text-gray-900">{item.book_name}</p>
                                <p className="text-sm text-gray-600">by {item.book_author}</p>
                                <p className="text-sm text-gray-600">Quantity: {item.quantity}</p>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-gray-900">৳{item.unit_price}</p>
                            <p className="text-sm text-gray-600">
                              Total: ৳{item.total_price}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Order Summary */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 mb-3">Order Summary</h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Subtotal:</span>
                        <span className="font-medium">৳{selectedOrder.subtotal}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-gray-600">Shipping Charge:</span>
                        <span className="font-medium">৳{selectedOrder.shipping_charge}</span>
                      </div>
                      {selectedOrder.promo_code && (
                        <div className="flex justify-between">
                          <span className="text-gray-600">Promo Code:</span>
                          <span className="font-medium text-green-600">{selectedOrder.promo_code}</span>
                        </div>
                      )}
                      <div className="flex justify-between text-lg font-bold border-t pt-2">
                        <span>Total:</span>
                        <span>৳{selectedOrder.total}</span>
                      </div>
                    </div>
                  </div>

                  {/* Payment Information */}
                  {selectedOrder.book_payment && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                        <CreditCard className="w-5 h-5" />
                        Payment Information
                      </h3>
                      <div className="space-y-2 text-sm">
                        <div className="flex justify-between">
                          <span className="text-gray-600">Payment Status:</span>
                          <div className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-xs font-medium ${getPaymentStatusColor(selectedOrder.book_payment.payment_status)}`}>
                            <CreditCard className="w-3 h-3" />
                            {selectedOrder.book_payment.payment_status}
                          </div>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-gray-600">Amount:</span>
                          <span className="font-medium">৳{selectedOrder.book_payment.amount}</span>
                        </div>
                        {selectedOrder.book_payment.gateway_transaction_id && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Transaction ID:</span>
                            <span className="font-medium text-xs break-all">{selectedOrder.book_payment.gateway_transaction_id}</span>
                          </div>
                        )}
                        {selectedOrder.book_payment.initiated_at && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Payment Initiated:</span>
                            <span className="font-medium">{selectedOrder.book_payment.initiated_at}</span>
                          </div>
                        )}
                        {selectedOrder.book_payment.completed_at && (
                          <div className="flex justify-between">
                            <span className="text-gray-600">Payment Completed:</span>
                            <span className="font-medium">{selectedOrder.book_payment.completed_at}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Notes */}
                  {selectedOrder.notes && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h3 className="font-semibold text-gray-900 mb-2">Notes</h3>
                      <p className="text-sm text-gray-600">{selectedOrder.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default MyOrder;